import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Play, StopCircle } from "lucide-react";
import { JudgmentList } from "./JudgmentList";

function ReplayProgress({ r }) {
  const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
  return (
    <Card className="p-4" data-testid={`bd-replay-${r.replay_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap text-sm">
        <div><span className="font-semibold">{r.replay_id}</span> · {r.days ? `last ${r.days} days` : `${r.total} email(s)`} · {r.mailbox} · <span className={r.status === "running" ? "text-amber-700" : r.status === "done" ? "text-emerald-700" : "text-rose-700"}>{r.status}</span></div>
        <div className="text-xs text-slate-500">{r.done}/{r.total} · judged {r.judged} · screened {r.screened} · failed {r.failed}</div>
      </div>
      <div className="h-1.5 bg-slate-100 rounded mt-2"><div className="h-1.5 bg-[#003E87] rounded" style={{ width: `${pct}%` }} /></div>
      {r.error && <div className="text-xs text-rose-700 mt-1">{r.error}</div>}
      {r.breakdown?.length ? <div className="flex flex-wrap gap-1.5 mt-2">{r.breakdown.map((b, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 border">{(b.category || "?").replace(/_/g, " ")} / {(b.fit || "?").replace(/_/g, " ")}: {b.n}</span>)}</div> : null}
    </Card>
  );
}

// Acceptance test step 1: replay the last 60 days in test mode (writes nothing live). Section 6: the one-time binding clean-up.
export function ReplayPanel({ canRun }) {
  const [replays, setReplays] = useState([]);
  const [sel, setSel] = useState(null);
  const [days, setDays] = useState(60);
  const [fixes, setFixes] = useState(null);
  const [checked, setChecked] = useState({});
  const load = () => api.get("/bd/replay").then((r) => { setReplays(r.data); if (!sel && r.data[0]) setSel(r.data[0].replay_id); }).catch(() => {});
  const loadFixes = () => api.get("/bd/binding-fixes").then((r) => { setFixes(r.data); setChecked(Object.fromEntries(r.data.filter((p) => p.keep).map((p) => [p.thread_id, true]))); }).catch(() => setFixes([]));
  useEffect(() => { load(); loadFixes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const running = replays.some((r) => r.status === "running");
    if (!running) return;
    const t = setInterval(load, 4000); return () => clearInterval(t);
  }, [replays]); // eslint-disable-line react-hooks/exhaustive-deps
  const [detail, setDetail] = useState(null);
  useEffect(() => { if (sel) api.get(`/bd/replay/${sel}`).then((r) => setDetail(r.data)).catch(() => setDetail(null)); }, [sel, replays]);

  const start = async () => {
    try { const r = await api.post("/bd/replay", { days: Number(days) }); toast.success(`Replay ${r.data.replay_id} started (test mode — nothing is written live)`); setSel(r.data.replay_id); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };
  const cancel = async (id) => { await api.post(`/bd/replay/${id}/cancel`); load(); };
  const applyFixes = async () => {
    const chosen = (fixes || []).filter((p) => checked[p.thread_id]);
    if (!chosen.length) return;
    if (!window.confirm(`Apply ${chosen.length} binding fix(es)? Emails are re-bound to the kept record; extra leads are marked superseded (never deleted).`)) return;
    try { const r = await api.post("/bd/binding-fixes/apply", { proposals: chosen }); toast.success(`${r.data.length} change(s) applied`); loadFixes(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };

  return (
    <div className="space-y-6">
      <section className="space-y-3" data-testid="bd-replay-section">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base md:text-lg font-semibold text-black">60-day replay (test mode)</h2>
          <span className="text-xs text-slate-500">Runs the mailbox through the judge and writes nothing live — the review table for the acceptance test.</span>
        </div>
        {canRun && (
          <div className="flex items-center gap-2">
            <Input type="number" value={days} onChange={(e) => setDays(e.target.value)} className="w-24" data-testid="bd-replay-days" />
            <span className="text-sm text-slate-600">days</span>
            <Button onClick={start} disabled={replays.some((r) => r.status === "running")} className="bg-[#003E87] hover:bg-[#002a5c] text-white" data-testid="bd-replay-start"><Play className="w-3.5 h-3.5 mr-1" /> Run replay</Button>
            {replays.filter((r) => r.status === "running").map((r) => <Button key={r.replay_id} variant="outline" onClick={() => cancel(r.replay_id)} data-testid={`bd-replay-cancel-${r.replay_id}`}><StopCircle className="w-3.5 h-3.5 mr-1" /> Stop</Button>)}
          </div>
        )}
        {replays.length === 0 ? <Card className="p-8 text-center text-sm text-slate-500" data-testid="bd-replay-empty">No replay has run yet.</Card> : (
          <div className="space-y-2">
            {replays.slice(0, 5).map((r) => (
              <button key={r.replay_id} onClick={() => setSel(r.replay_id)} className={`block w-full text-left rounded-lg ${sel === r.replay_id ? "ring-2 ring-[#003E87]/40" : ""}`} data-testid={`bd-replay-pick-${r.replay_id}`}>
                <ReplayProgress r={sel === r.replay_id && detail ? detail : r} />
              </button>
            ))}
          </div>
        )}
        {sel && <JudgmentList replayId={sel} />}
      </section>

      <section className="space-y-3" data-testid="bd-fixes-section">
        <h2 className="text-base md:text-lg font-semibold text-black">One thread, one pursuit — clean-up</h2>
        <p className="text-xs text-slate-500">Threads bound to more than one record where the thread is really about one project. Bundles (mechanical + electrical of one project), multi-project threads and history are left alone. Confirm the ones to fix; only the emails that name a different project are re-bound and extra leads are marked superseded (never deleted).</p>
        {fixes === null ? <div className="text-sm text-slate-400">Loading…</div> : !fixes.length ? <Card className="p-6 text-sm text-slate-500" data-testid="bd-fixes-empty">No thread is bound to more than one record.</Card> : (
          <div className="space-y-2" data-testid="bd-fixes-list">
            {fixes.map((p) => (
              <Card key={p.thread_id} className="p-3 flex items-start gap-3" data-testid={`bd-fix-${p.thread_id}`}>
                <Checkbox checked={!!checked[p.thread_id]} onCheckedChange={(v) => setChecked({ ...checked, [p.thread_id]: !!v })} className="mt-1" data-testid={`bd-fix-check-${p.thread_id}`} />
                <div className="min-w-0 text-sm">
                  <div className="font-medium text-black truncate">{p.subject || p.thread_id} <span className="text-slate-400 font-normal">· {p.counterparty}</span></div>
                  <div className="text-xs text-slate-600 mt-1">{p.keep ? <>Keep <span className="font-semibold">{p.keep.name || p.keep.id}</span> ({p.keep.type}). Unbind: {(p.rebind || []).map((id) => { const r = p.records.find((x) => x.id === id) || { id }; return `${r.name || r.id} (${r.type || "?"}${r.status ? `, ${r.status}` : ""})`; }).join("; ") || "—"} · {(p.rebind_emails || []).length} email(s) re-bound</> : <span className="text-amber-700">Needs a person to read it</span>}</div>
                  <div className="text-xs text-slate-500 mt-0.5" data-testid={`bd-fix-reason-${p.thread_id}`}>{p.reason}</div>
                </div>
              </Card>
            ))}
            {canRun && <Button onClick={applyFixes} className="bg-[#003E87] hover:bg-[#002a5c] text-white" data-testid="bd-fixes-apply">Apply the confirmed fixes</Button>}
          </div>
        )}
      </section>
    </div>
  );
}
